import React, { useEffect, useState } from 'react'
import { filter } from 'lodash-es'
import { Router, useRouter } from 'next/router'

import { Card, useTheme } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import Divider from '@mui/material/Divider'

import Link from '../Common/Link'
import SubCategory from './SubCategory'

import { Category } from '@/src/types/api-types'
import getRoutes from '@/lib/getRoutes'
import SETTINGS from '@/src/settings'
import Image from 'next/image'

export type CategoryExtended = Category & {
  route?: string
  forceColor?: any
  icon?: any
  disabled?: boolean
}

type Props = {
  category: CategoryExtended
  categories: Category[]
  mobile?: boolean
}

const MainCategory: React.FC<Props> = ({ category, categories, mobile = false }) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const [openSub, setOpenSub] = useState<boolean>(false)

  const router = useRouter()

  const route = category.route || 'Categories'

  const handleMouseEnter = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMouseOut = () => {
    setAnchorEl(null)
  }

  const handleClick = () => {
    setOpenSub(!openSub)
  }

  // Même logique que SubCategory (menu déroulant) : catégorie + ses sous-catégories directes,
  // pour que le menu fixe et le menu déroulant filtrent à l'identique.
  // Valeur brute (pas d'encodeURIComponent) : getRoutes/query-string encode une seule fois (sinon double encodage).
  const getFeaturesHash = cat => {
    const directSubCategories = filter(
      categories,
      c => Number(c.parent_id) === Number(cat?.category_id)
    )
    const categoriesToInclude = [cat?.category, ...directSubCategories.map(subCat => subCat.category)]
    return categoriesToInclude
      .filter(Boolean)
      .map(catName => `Catégories-${catName}`)
      .join('_')
  }

  useEffect(() => {
    Router.events.on('routeChangeStart', handleMouseOut)
  }, [])

  const open = Boolean(anchorEl)

  const subCategories = filter(
    categories,
    c => Number(c.parent_id) === Number(category.category_id)
  ).sort((a, b) => (a.position > b.position ? 1 : -1))

  const routeAssociated = getRoutes({ route })
  const actualRoute = routeAssociated.href.split('?')[0]

  const getRoute = getRoutes({ route })

  const regexNameRoute = new RegExp(
    getRoute.as.replace('/', '').replace(/[!@#$%^&*()+=\-[\]\\';,./{}|":<>?~_]/gi, '\\$&'),
    'i'
  )

  const catetogorieSelect =
    (!!router.query.slug &&
      filter(
        subCategories,
        c => c.seo_name === router.query.slug && c.category_id != SETTINGS.categoryAntiGaspi
      ).length) ||
    (!!category.category_id &&
      category.seo_name === router.query.slug &&
      actualRoute === router.route) ||
    regexNameRoute.test(router.asPath)

  const titleCategory = category?.icon ? (
    <Image src={category.icon} alt={SETTINGS.seo.title} width={160} height={35} />
  ) : (
    category.category
  )

  // Lien vers la destination de la categorie (reutilise par la ligne sans sous-menu
  // et par l'entree "Voir tout" du sous-menu deplie)
  const mobileCategoryLink = (label: string) =>
    route === 'Categories' ? (
      <Link
        route="Catalogsearch"
        argsObject={{ query: '', featuresHash: getFeaturesHash(category) }}
        className="cursor-pointer"
      >
        <ListItemText primary={label} />
      </Link>
    ) : (
      <Link route={route} values={{ '[slug]': category.seo_name }}>
        <ListItemText primary={label} />
      </Link>
    )

  return mobile ? (
    <>
      {subCategories.length > 0 ? (
        // Tap sur toute la ligne = deplier/replier (pas de navigation, sinon le
        // drawer se ferme via routeChangeStart) ; la navigation reste accessible
        // via "Voir tout" en tete du sous-menu
        <ListItem button onClick={handleClick}>
          <ListItemText primary={category.category} />
          {openSub ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
      ) : (
        <ListItem>{mobileCategoryLink(category.category)}</ListItem>
      )}
      <Divider />
      <Collapse in={openSub} timeout="auto" unmountOnExit style={{ paddingLeft: 16 }}>
        <ListItem button>{mobileCategoryLink(t({ id: 'web_see_all' }))}</ListItem>
        <Divider />
        {subCategories.map(subCat => (
          <SubCategory
            key={`${category?.category_id || category.category}-${subCat.category_id}`}
            category={subCat}
            categories={categories}
            mobile
          />
        ))}
      </Collapse>
    </>
  ) : (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseOut}
      style={{ position: 'relative', backgroundColor: '#FFFFFF' }}
    >
      {route === 'Categories' ? (
        <Link
          route="Catalogsearch"
          argsObject={{ query: '', featuresHash: getFeaturesHash(category) }}
          className="cursor-pointer"
        >
          <div
            className="h-[2.8rem] leading-[2.8rem] text-center w-full flex justify-center items-center hover:bg-gray-100"
            style={{ color: category.forceColor || theme.palette.text.primary }}
          >
            {titleCategory}
          </div>
          <span
            className={
              catetogorieSelect ? 'absolute h-[5px] bottom-0 w-full rounded-t-2xl' : undefined
            }
            style={catetogorieSelect ? { backgroundColor: theme.palette.text.primary } : undefined}
          ></span>
        </Link>
      ) : (
        <Link route={route} values={{ '[slug]': category.seo_name }}>
          <div
            className="h-[2.8rem] leading-[2.8rem] text-center w-full flex justify-center items-center hover:bg-gray-100"
            style={{ color: category.forceColor || theme.palette.text.primary }}
          >
            {titleCategory}
          </div>
          <span
            className={
              catetogorieSelect ? 'absolute h-[5px] bottom-0 w-full rounded-t-2xl' : undefined
            }
            style={catetogorieSelect ? { backgroundColor: theme.palette.text.primary } : undefined}
          ></span>
        </Link>
      )}
      {subCategories.length > 0 && open && (
        <Card style={{ position: 'absolute', zIndex: 1300, overflow: 'visible' }}>
          {subCategories.map((subCat, index) => (
            <SubCategory
              key={`${category?.category_id || category.category}-${subCat.category_id}`}
              index={index}
              category={subCat}
              categories={categories}
            />
          ))}
        </Card>
      )}
    </div>
  )
}

export default MainCategory
